import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
    Box, Typography, Button, IconButton, Stack, Chip, Avatar, Tooltip,
    Table, TableHead, TableRow, TableCell, TableBody, Skeleton,
    Dialog, DialogTitle, DialogContent, DialogActions,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBackOutlined";
import ForumIcon from "@mui/icons-material/ForumOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { toast } from "react-toastify";
import api, { apiError } from "../api";
import AdminHeader, { adminPage } from "../components/AdminHeader";
import AppFooter from "../components/AppFooter";

/*
 * One customer and the licences they have issued - one per plant, each with its own plan
 * and its own daily budget. The Conversations button opens what the assistant was asked
 * at that site.
 */


const card = {
    borderRadius: 1.5, bgcolor: "rgba(255,255,255,.04)",
    border: "1px solid rgba(255,255,255,.12)",
    overflowX: "auto",
};
const th = {
    color: "rgba(255,255,255,.72)", fontSize: 12, fontWeight: 700,
    textTransform: "uppercase", letterSpacing: .6, whiteSpace: "nowrap",
    bgcolor: "rgba(255,255,255,.06)",
    borderBottom: "1px solid rgba(255,255,255,.18)",
};
const td = { color: "#fff", borderColor: "rgba(255,255,255,.07)" };

const dialogPaper = {
    sx: {
        bgcolor: "#0C1A12", color: "#fff", borderRadius: 3,
        border: "1px solid rgba(255,255,255,.12)", backgroundImage: "none",
    },
};

// The same round action button as the licences table, so the one act they share looks
// the same in both places.
const actionAvatar = (bg) => ({ width: 28, height: 28, bgcolor: bg, color: "#fff" });

const chip = (tone) => ({
    bgcolor: tone === "good" ? "rgba(46,125,32,.25)"
        : tone === "warn" ? "rgba(214,158,46,.2)" : "rgba(255,255,255,.08)",
    color: tone === "good" ? "#9FE08C" : tone === "warn" ? "#F3C969" : "rgba(255,255,255,.75)",
    border: "1px solid rgba(255,255,255,.14)", fontWeight: 600, textTransform: "capitalize",
});

export default function AdminAccountDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [account, setAccount] = useState(null);
    const [toks, setToks] = useState([]);
    const [doomed, setDoomed] = useState(null);   // being deleted, pending confirmation
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const { data } = await api.get(`/console/accounts/${id}`);
            setAccount(data.account);
            setToks(data.tokens || []);
        } catch (e) { toast.error(apiError(e)); }
    }, [id]);

    useEffect(() => { load(); }, [load]);

    const remove = async () => {
        setBusy(true);
        try {
            const { data } = await api.delete(`/console/tokens/${doomed._id}`);
            toast.success(data.msg || "Token deleted");
            setDoomed(null);
            load();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(false); }
    };



    return (
        <Box sx={{ ...adminPage, display: "flex", flexDirection: "column" }}>
            <AdminHeader />

            <Box sx={{ px: { xs: 2, md: 4 }, py: { xs: 3, md: 4 } }}>
                {!account ? (
                    <Stack spacing={2}>
                        <Skeleton height={60} sx={{ bgcolor: "rgba(255,255,255,.06)" }} />
                        <Skeleton height={240} variant="rectangular"
                            sx={{ bgcolor: "rgba(255,255,255,.06)", borderRadius: 2 }} />
                    </Stack>
                ) : (
                    <>
                        {/* Just who this is. The plan and limit that used to be editable
                            here belonged to the account, and an account no longer has a
                            plan - each token does, and that is the table below. */}
                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5}
                            sx={{ alignItems: { sm: "baseline" }, mb: 2.5 }}>
                            <IconButton onClick={() => navigate("/admin/accounts")}
                                sx={{
                                    color: "rgba(255,255,255,.75)",
                                    border: "1px solid rgba(255,255,255,.14)",
                                    bgcolor: "rgba(255,255,255,.04)",
                                    "&:hover": { bgcolor: "rgba(255,255,255,.09)" },
                                    alignSelf: { sm: "center" },
                                }}>
                                <ArrowBackIcon fontSize="small" />
                            </IconButton>
                            <Typography sx={{ fontWeight: 800, fontSize: 22 }}>{account.email}</Typography>
                            <Typography sx={{ color: "rgba(255,255,255,.6)", fontSize: 14 }}>
                                {account.name || "—"}
                            </Typography>
                        </Stack>

                        {/* What this customer has actually issued. Above the pairing table
                            because it is where every plant comes from now. */}
                        <Box sx={{ ...card, overflow: "hidden", mb: 2.5 }}>
                            <Table size="small" sx={{ tableLayout: "fixed", minWidth: 760 }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={th} align="center">Action</TableCell>
                                        <TableCell sx={th} align="center">Email</TableCell>
                                        <TableCell sx={th} align="center">Plan</TableCell>
                                        <TableCell sx={th} align="center">Status</TableCell>
                                        <TableCell sx={th} align="center">Created</TableCell>
                                        <TableCell sx={th} align="center">Conversations</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {toks.map((t) => (
                                        <TableRow key={t._id}>
                                            <TableCell sx={td} align="center">
                                                <Tooltip title="Delete this token">
                                                    <IconButton sx={{ p: .3 }} onClick={() => setDoomed(t)}>
                                                        <Avatar sx={actionAvatar("#8C2F2F")}>
                                                            <DeleteIcon sx={{ fontSize: 16 }} />
                                                        </Avatar>
                                                    </IconButton>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell sx={{ ...td, fontWeight: 600 }} align="center">{t.email}</TableCell>
                                            <TableCell sx={td} align="center">
                                                <Chip size="small" label={t.plan}
                                                    sx={chip(t.plan === "lite" ? "plain" : "good")} />
                                            </TableCell>
                                            <TableCell sx={td} align="center">
                                                <Chip size="small" sx={chip(t.status === "active" ? "good" : "plain")}
                                                    label={t.status === "active" ? "Active" : "Deactive"} />
                                            </TableCell>
                                            <TableCell sx={{ ...td, color: "rgba(255,255,255,.65)" }} align="center">
                                                {new Date(t.createdAt).toLocaleString("en-IN", { hour12: false })}
                                            </TableCell>
                                            <TableCell sx={td} align="center">
                                                {/* Straight to this plant's conversations, with
                                                    the count on it. Dead when there are none -
                                                    a button that opens an empty page teaches
                                                    people to stop trusting the button. */}
                                                <Button size="small" disabled={!t.chats}
                                                    startIcon={<ForumIcon sx={{ fontSize: 16 }} />}
                                                    onClick={() => navigate(`/admin/chats?licence=c:${t._id}`)}
                                                    sx={{
                                                        borderRadius: 999, px: 2, minWidth: 78,
                                                        color: "#9FE08C",
                                                        border: "1px solid rgba(123,217,95,.35)",
                                                        "&:hover": { bgcolor: "rgba(46,125,32,.18)" },
                                                        "&.Mui-disabled": {
                                                            color: "rgba(255,255,255,.35)",
                                                            border: "1px solid rgba(255,255,255,.12)",
                                                        },
                                                    }}>
                                                    {t.chats || 0}
                                                </Button>
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    {!toks.length && (
                                        <TableRow>
                                            <TableCell colSpan={6} align="center"
                                                sx={{ ...td, py: 4, color: "rgba(255,255,255,.5)" }}>
                                                This customer has not created a token yet
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </TableBody>
                            </Table>
                        </Box>

                    </>
                )}
            </Box>

            {/* ---------- delete ---------- */}
            <Dialog open={Boolean(doomed)} onClose={() => setDoomed(null)} maxWidth="xs" fullWidth
                slotProps={{ paper: dialogPaper }}>
                <DialogTitle sx={{ fontWeight: 700 }}>Delete this token?</DialogTitle>
                <DialogContent>
                    <Typography sx={{ color: "rgba(255,255,255,.7)", fontSize: 14.5 }}>
                        The token for <b>{doomed?.email}</b> will be removed. That plant's
                        assistant stops answering the moment it goes.
                    </Typography>
                    <Typography sx={{ color: "#F3C969", fontSize: 13.5, mt: 1.5 }}>
                        Its Razorpay subscription is cancelled so nothing is charged again, and
                        its {doomed?.chats ? `${doomed.chats} ` : ""}saved conversations are
                        deleted with it. Invoices already raised are kept.
                    </Typography>
                    <Typography sx={{ color: "rgba(255,255,255,.5)", fontSize: 13, mt: 1.5 }}>
                        This cannot be undone.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2.5 }}>
                    <Button onClick={() => setDoomed(null)} sx={{ color: "rgba(255,255,255,.65)" }}>Cancel</Button>
                    <Button variant="contained" disabled={busy} onClick={remove}
                        sx={{ borderRadius: 999, px: 3, bgcolor: "#B3261E", "&:hover": { bgcolor: "#8F1D17" } }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            <AppFooter />
        </Box>
    );
}
